import {
  Review,
  PaginatedResponse,
  ReviewListParams,
  ModerateReviewRequest,
  BulkModerateRequest,
  BulkModerateResponse,
  DepotEvent,
  EventListParams,
  CreateEventRequest,
  UpdateEventRequest,
  CreateEventSeriesRequest,
  CreateEventSeriesResult,
  RejectEventRequest,
  ReopenEventRequest,
  NotifyAdvertisingDepotsResult,
  EventType,
  PresetText,
  SalesChannel,
  DepotRef,
  EventText,
  EventTextDeleteResult,
  EventTextUsageResponse,
  CancellationTerm,
  CancellationTermDeleteResult,
  CancellationTermUsageResponse,
  SaveCancellationTermRequest,
  PaymentNoteTemplate,
  PaymentNoteTemplateDeleteResult,
  PaymentNoteTemplateUsageResponse,
  SavePaymentNoteTemplateRequest,
  FinancialSupport,
  FinancialSupportDeleteResult,
  SaveFinancialSupportRequest,
  RegistrationNoteTemplate,
  RegistrationNoteTemplateDeleteResult,
  RegistrationNoteTemplateUsageResponse,
  SaveRegistrationNoteTemplateRequest,
  Mailing,
  MailingDeleteResult,
  MailingImportResult,
  ImportJobStatusFile,
  UploadEventsImportResult,
  UploadUrlResult,
  RollbackImportResult,
  ManagedUser,
  ParticipantsResponse,
  Partner,
  PermissionCatalog,
  RefundView,
  RefundDraft,
  AddRefundParticipantInput,
  Settlement,
  TicketRefund,
  WineProduct,
  TaxCategoryKey,
  CustomerSearchResult,
  RoleSwitchResult,
  Role,
  SaveEventTextRequest,
  SaveMailingRequest,
} from '@/types';
import {
  getMockReviews,
  moderateMockReview,
  bulkModerateMockReviews,
  deleteMockReview,
} from './mock-data';
import {
  getMockEvents,
  getMockEvent,
  getMockEventTypes,
  getMockPresetTexts,
  getMockSalesChannels,
  getMockMailings,
  saveMockMailing,
  deleteMockMailings,
  getMockEventTexts,
  saveMockEventText,
  deleteMockEventTexts,
  getMockCancellationTerms,
  saveMockCancellationTerm,
  deleteMockCancellationTerms,
  getMockPaymentNoteTemplates,
  saveMockPaymentNoteTemplate,
  deleteMockPaymentNoteTemplates,
  getMockFinancialSupport,
  saveMockFinancialSupport,
  deleteMockFinancialSupport,
  getMockRegistrationNoteTemplates,
  saveMockRegistrationNoteTemplate,
  deleteMockRegistrationNoteTemplates,
  getMockDepots,
  createMockEvent,
  createMockEventSeries,
  updateMockEvent,
  deleteMockEvent,
  submitMockEvent,
  approveMockEvent,
  rejectMockEvent,
  reopenMockEvent,
  cancelMockEvent,
  completeMockEvent,
  notifyAdvertisingDepotsMockEvent,
} from './mock-data-events';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const IS_MOCK = import.meta.env.VITE_API_MOCK === 'true';
const MOCK_DELAY = 300; // simulate network latency
/** localStorage key for the staging role-switch token — see ApiClient.setAssumedRoleToken(). */
const ASSUMED_ROLE_TOKEN_KEY = 'adminAppAssumedRoleToken';
/**
 * localStorage key for the dev/staging-only settlement-mode override — see
 * ApiClient.setForceAutomaticSettlement(). Deliberately a SEPARATE key from
 * ASSUMED_ROLE_TOKEN_KEY: a role switch overwrites/clears that key and does a
 * full page reload, but must NOT clear this one — the whole point is to keep
 * forcing automatic settlement mode across a switch to Depot-Partner.
 */
const FORCE_AUTOMATIC_SETTLEMENT_KEY = 'adminAppForceAutomaticSettlement';

/**
 * Reads a File into a base64 string.
 *
 * Uses FileReader rather than arrayBuffer + btoa: btoa operates on a binary
 * string and a spreadsheet is not valid Latin-1, so the naive conversion
 * corrupts bytes above 0x7F.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Datei konnte nicht gelesen werden'));
        return;
      }
      // Strip the "data:<mime>;base64," prefix the reader prepends.
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Datei konnte nicht gelesen werden'));
    reader.readAsDataURL(file);
  });
}

function mockDelay(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, MOCK_DELAY));
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Dev-only identity, sent as X-Role / X-Depots headers (PRD-018 §4.4). */
export interface DevIdentity {
  role: string;
  depotKeys: string[];
  userId: string;
  email: string;
}

/** Response of GET /api/me — mirrors MeResponse in the backend. */
export interface MeResponse {
  email: string;
  name: string;
  userId: string;
  role: string;
  /** Depot keys the user may act on; empty for central roles (= all depots). */
  depots: string[];
  /**
   * Actions per app, as resolved server-side.
   *
   * Open record rather than a fixed pair: a new app must not require a change to
   * this contract. Callers read `permissions[resource] ?? []`.
   */
  permissions: Record<string, string[]>;
  /**
   * Welche Stufe der Auflösungskette die Rolle geliefert hat.
   *
   * `directory` kam mit PRD-021 dazu: dieselbe Auflösung wie `allowlist`, aber an erster
   * Stelle statt als letzter Rückfall. Beide Namen bleiben, damit im Protokoll erkennbar
   * ist, welcher Weg gegriffen hat.
   */
  roleSource: 'entra-group' | 'commercetools' | 'allowlist' | 'assumed' | 'directory';
  /**
   * Partnernummer der Depot-Rolle, sofern auflösbar.
   *
   * Damit entscheidet die Vorlagenpflege ohne zweiten Abruf, welche Vorlagen bearbeitbar
   * sind (US-204). Fehlt bei zentralen Rollen. ⚠️ Anzeigehilfe — der Server prüft dieselbe
   * Regel selbst und antwortet mit 403.
   */
  partnerKey?: string;
  /**
   * Whether this user may switch role for testing (staging).
   *
   * Already combines the per-user right and the environment flag, so the UI can
   * render on this alone. The server re-checks on every switch.
   */
  canSwitchRole?: boolean;
  /**
   * Whether this user may toggle the dev/staging-only settlement-mode
   * override (DCP-2228 follow-up, 19.09.2026). Rendering only — der Server
   * prüft Umgebung + Rolle selbst erneut.
   */
  canOverrideSettlementMode?: boolean;
  /**
   * Aktuell wirksame Abrechnungsart (heutiges Datum, inklusive Override,
   * falls aktiv) — siehe ApiClient.setForceAutomaticSettlement().
   */
  settlementMode?: 'manual' | 'automatic';
}

class ApiClient {
  private baseUrl: string;
  private getAccessToken?: () => Promise<string>;
  private devIdentity?: DevIdentity;
  private assumedRoleToken?: string;
  private forceAutomaticSettlement: boolean;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Bug fix 22.09.2026: switchRole() used to hold the token only in this
    // in-memory field, then immediately trigger a full page reload (see
    // StagingRoleSwitcher.tsx — needed because the assumed role changes what
    // nearly every screen may show). The reload re-creates this instance from
    // scratch, wiping the field before a single request ever carried the new
    // token — every request after "switching", including the first GET /me,
    // silently went out under the real role again. The token itself is
    // already scoped server-side (signed, 8h TTL, bound to the signed-in
    // user's email — see assumed-role.ts), so surviving in localStorage adds
    // no new risk; it only makes the switch outlive the reload it needs.
    this.assumedRoleToken = localStorage.getItem(ASSUMED_ROLE_TOKEN_KEY) ?? undefined;
    // Eigener Key statt ASSUMED_ROLE_TOKEN_KEY (DCP-2228 follow-up,
    // 19.09.2026) — muss einen Rollenwechsel-Reload überleben, siehe
    // FORCE_AUTOMATIC_SETTLEMENT_KEY.
    this.forceAutomaticSettlement = localStorage.getItem(FORCE_AUTOMATIC_SETTLEMENT_KEY) === 'true';
  }

  setAuthProvider(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken;
  }

  /**
   * Sets the mock identity forwarded to the BFF while auth is disabled.
   *
   * Without this the backend never learns which role/depots the RoleSwitcher
   * selected, falls back to its own mock user, and returns events across all
   * depots — server-side depot scoping would go untested. The backend only
   * honours these headers when AUTH_ENABLED is false.
   */
  setDevIdentity(identity: DevIdentity | undefined) {
    this.devIdentity = identity;
  }

  /**
   * Sets (or clears) the assumed-role token from the staging role switch.
   *
   * Sent as X-Assumed-Role on every later request. The server verifies the
   * signature and that the token belongs to the signed-in user, so holding it here
   * grants nothing on its own — it only tells the backend which of the roles this
   * user is allowed to assume they are currently using.
   *
   * Persisted to localStorage (22.09.2026-Follow-up) so it survives the full
   * reload that always follows a switch — see the constructor's comment.
   */
  setAssumedRoleToken(token: string | undefined) {
    this.assumedRoleToken = token;
    if (token) {
      localStorage.setItem(ASSUMED_ROLE_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(ASSUMED_ROLE_TOKEN_KEY);
    }
  }

  /**
   * Sets (or clears) the dev/staging-only settlement-mode override.
   *
   * Sent as X-Force-Automatic-Settlement on every later request. The server
   * ignores the header unless SETTLEMENT_OVERRIDE_ENABLED is set (never in
   * production), so holding it here grants nothing on its own. Persisted
   * under its own localStorage key, deliberately independent from the
   * assumed-role token — it must keep being sent after switching to
   * Depot-Partner, not just for the tester's own Admin/Zentral role.
   */
  setForceAutomaticSettlement(value: boolean) {
    this.forceAutomaticSettlement = value;
    if (value) {
      localStorage.setItem(FORCE_AUTOMATIC_SETTLEMENT_KEY, 'true');
    } else {
      localStorage.removeItem(FORCE_AUTOMATIC_SETTLEMENT_KEY);
    }
  }

  /** Current value of the settlement-mode override, for the settings toggle. */
  isForcingAutomaticSettlement(): boolean {
    return this.forceAutomaticSettlement;
  }

  /**
   * Auth-Header-Aufbau, gemeinsam für request() (JSON) und getRefundProofBlob()
   * (Binär-Download, DCP-2228 follow-up 15.09.2026) — ein reiner Blob-Download
   * passt nicht durch request()s response.json()-Annahme, braucht aber
   * dieselbe Authentifizierung.
   */
  private async buildAuthHeaders(base: Record<string, string> = {}): Promise<Record<string, string>> {
    const headers: Record<string, string> = { ...base };

    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get access token:', error);
        throw new ApiError(401, 'Authentication failed');
      }
    }

    if (this.assumedRoleToken) {
      headers['X-Assumed-Role'] = this.assumedRoleToken;
    }

    if (this.forceAutomaticSettlement) {
      headers['X-Force-Automatic-Settlement'] = 'true';
    }

    if (this.devIdentity) {
      headers['X-Role'] = this.devIdentity.role;
      headers['X-Depots'] = this.devIdentity.depotKeys.join(',');
      headers['X-User-Id'] = this.devIdentity.userId;
      headers['X-Email'] = this.devIdentity.email;
    }

    return headers;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Record rather than HeadersInit: the latter is a union that includes
    // Headers and string[][], so it is not indexable — assigning single headers
    // below would be a type error. fetch accepts a plain record just fine.
    const headers = await this.buildAuthHeaders({
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    });

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // The events endpoints answer {error:{code,message}} while the reviews ones
      // put `message` at the top level. Read both, nested first, or a user-facing
      // message like FILTER_TOO_BROAD's arrives as a bare "HTTP 400".
      const message =
        errorData.error?.message ||
        errorData.message ||
        `HTTP ${response.status}: ${response.statusText}`;
      throw new ApiError(response.status, message, errorData);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // Review endpoints
  async getReviews(
    params: ReviewListParams
  ): Promise<PaginatedResponse<Review>> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockReviews(params);
    }

    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.search) queryParams.append('search', params.search);

    if (params.status) {
      params.status.forEach(s => queryParams.append('status', s));
    }
    if (params.moderationStatus) {
      params.moderationStatus.forEach(ms => queryParams.append('moderationStatus', ms));
    }
    if (params.approvalFilter) {
      params.approvalFilter.forEach(af => queryParams.append('approvalFilter', af));
    }
    if (params.flagTypes) {
      params.flagTypes.forEach(ft => queryParams.append('flagTypes', ft));
    }
    if (params.rating) {
      params.rating.forEach(r => queryParams.append('rating', r.toString()));
    }
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);

    return this.request<PaginatedResponse<Review>>(
      `/reviews?${queryParams.toString()}`
    );
  }

  async getReview(reviewId: string): Promise<Review> {
    if (IS_MOCK) {
      await mockDelay();
      const result = getMockReviews({ page: 1, pageSize: 100 });
      const review = result.items.find((r) => r.reviewId === reviewId);
      if (!review) throw new ApiError(404, 'Review not found');
      return review;
    }

    return this.request<Review>(`/reviews/${reviewId}`);
  }

  async moderateReview(request: ModerateReviewRequest): Promise<Review> {
    if (IS_MOCK) {
      await mockDelay();
      return moderateMockReview(request.reviewId, request.action);
    }

    return this.request<Review>(`/reviews/${request.reviewId}/moderate`, {
      method: 'POST',
      body: JSON.stringify({
        action: request.action,
        notes: request.notes,
      }),
    });
  }

  async bulkModerateReviews(
    request: BulkModerateRequest
  ): Promise<BulkModerateResponse> {
    if (IS_MOCK) {
      await mockDelay();
      return bulkModerateMockReviews(request.reviewIds, request.action);
    }

    return this.request<BulkModerateResponse>('/reviews/bulk-moderate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async deleteReview(reviewId: string): Promise<void> {
    if (IS_MOCK) {
      await mockDelay();
      deleteMockReview(reviewId);
      return;
    }

    await this.request<void>(`/reviews/${reviewId}`, {
      method: 'DELETE',
    });
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health');
  }

  /**
   * The caller's identity, role, depots and permissions.
   *
   * Authoritative: the backend evaluates the allowlist and role matrix, so the
   * frontend no longer ships (or can bypass) that logic.
   */
  async getMe(): Promise<MeResponse> {
    return this.request<MeResponse>('/me');
  }

  // ---- Veranstaltungen (Events) endpoints ----
  async getEvents(
    params: EventListParams
  ): Promise<PaginatedResponse<DepotEvent>> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockEvents(params);
    }

    const q = new URLSearchParams();
    if (params.page) q.append('page', params.page.toString());
    if (params.pageSize) q.append('pageSize', params.pageSize.toString());
    if (params.sortBy) q.append('sortBy', params.sortBy);
    if (params.sortOrder) q.append('sortOrder', params.sortOrder);
    if (params.search) q.append('search', params.search);
    if (typeof params.year === 'number') q.append('year', params.year.toString());
    if (typeof params.month === 'number') q.append('month', params.month.toString());
    if (params.dateFrom) q.append('dateFrom', params.dateFrom);
    if (params.dateTo) q.append('dateTo', params.dateTo);
    if (params.endedBefore) q.append('endedBefore', params.endedBefore);
    if (params.status) params.status.forEach((s) => q.append('status', s));
    if (params.origin) params.origin.forEach((o) => q.append('origin', o));
    if (params.audience) params.audience.forEach((v) => q.append('audience', v));
    if (params.depotKeys) params.depotKeys.forEach((d) => q.append('depotKeys', d));
    if (params.vaTypes) params.vaTypes.forEach((v) => q.append('vaTypes', v));
    if (params.refundStatus) params.refundStatus.forEach((r) => q.append('refundStatus', r));
    if (params.lowContingent) q.append('lowContingent', 'true');

    return this.request<PaginatedResponse<DepotEvent>>(
      `/events?${q.toString()}`
    );
  }

  async getEvent(identifier: string): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      try {
        return getMockEvent(identifier);
      } catch {
        throw new ApiError(404, 'Event not found');
      }
    }
    return this.request<DepotEvent>(`/events/${identifier}`);
  }

  async createEvent(request: CreateEventRequest): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return createMockEvent(request);
    }
    return this.request<DepotEvent>('/events', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * POST /api/events/:id/series (24.09.2026) — creates a series of new
   * events cloned from `identifier`. A 400 (invalid occurrence) surfaces as
   * an ApiError whose `.data.occurrenceErrors` carries the per-occurrence
   * field errors; the mock branch throws a plain Error instead (see
   * createMockEventSeries), so the dialog's catch handles both.
   */
  async createEventSeries(
    identifier: string,
    request: CreateEventSeriesRequest
  ): Promise<CreateEventSeriesResult> {
    if (IS_MOCK) {
      await mockDelay();
      const source = getMockEvent(identifier);
      return createMockEventSeries(source, request.occurrences, request.createAs);
    }
    return this.request<CreateEventSeriesResult>(`/events/${identifier}/series`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async updateEvent(request: UpdateEventRequest): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return updateMockEvent(request);
    }
    return this.request<DepotEvent>(`/events/${request.identifier}`, {
      method: 'PUT',
      body: JSON.stringify(request),
    });
  }

  async deleteEvent(identifier: string): Promise<void> {
    if (IS_MOCK) {
      await mockDelay();
      deleteMockEvent(identifier);
      return;
    }
    await this.request<void>(`/events/${identifier}`, {
      method: 'DELETE',
    });
  }

  async submitEvent(identifier: string): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return submitMockEvent(identifier);
    }
    return this.request<DepotEvent>(`/events/${identifier}/submit`, {
      method: 'POST',
    });
  }

  async approveEvent(identifier: string): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return approveMockEvent(identifier);
    }
    return this.request<DepotEvent>(`/events/${identifier}/approve`, {
      method: 'POST',
    });
  }

  async rejectEvent(request: RejectEventRequest): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return rejectMockEvent(request.identifier, request.reason);
    }
    return this.request<DepotEvent>(`/events/${request.identifier}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason: request.reason }),
    });
  }

  async reopenEvent(request: ReopenEventRequest): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return reopenMockEvent(request.identifier, request.toStatus);
    }
    return this.request<DepotEvent>(`/events/${request.identifier}/reopen`, {
      method: 'POST',
      body: JSON.stringify({ toStatus: request.toStatus }),
    });
  }

  async cancelEvent(identifier: string): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return cancelMockEvent(identifier);
    }
    return this.request<DepotEvent>(`/events/${identifier}/cancel`, {
      method: 'POST',
    });
  }

  async completeEvent(identifier: string): Promise<DepotEvent> {
    if (IS_MOCK) {
      await mockDelay();
      return completeMockEvent(identifier);
    }
    return this.request<DepotEvent>(`/events/${identifier}/complete`, {
      method: 'POST',
    });
  }

  async notifyAdvertisingDepots(identifier: string): Promise<NotifyAdvertisingDepotsResult> {
    if (IS_MOCK) {
      await mockDelay();
      return notifyAdvertisingDepotsMockEvent(identifier);
    }
    return this.request<NotifyAdvertisingDepotsResult>(
      `/events/${identifier}/notify-advertising-depots`,
      { method: 'POST' }
    );
  }

  async getEventTypes(): Promise<EventType[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockEventTypes();
    }
    return this.request<EventType[]>('/event-types');
  }

  async getPresetTexts(): Promise<PresetText[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockPresetTexts();
    }
    return this.request<PresetText[]>('/event-preset-texts');
  }

  async getSalesChannels(): Promise<SalesChannel[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockSalesChannels();
    }
    return this.request<SalesChannel[]>('/sales-channels');
  }

  async getMailings(): Promise<Mailing[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockMailings();
    }
    return this.request<Mailing[]>('/mailings');
  }

  /**
   * Alle Textvorlagen.
   *
   * Serverseitig ohne Rollenwache: der VA-Dialog füllt daraus die Auswahlfelder
   * Kategorie und Vorlage, für jede Rolle.
   */
  async getEventTexts(): Promise<EventText[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockEventTexts();
    }
    return this.request<EventText[]>('/event-texts');
  }

  /**
   * Legt eine Vorlage an oder ändert sie (Zentrale/Admin).
   *
   * Ohne `id` wird angelegt. Beim Ändern reist `etag` mit; passt er nicht mehr, kommt
   * ein 409 zurück, statt den Text eines anderen zu überschreiben.
   */
  async saveEventText(req: SaveEventTextRequest): Promise<EventText> {
    if (IS_MOCK) {
      await mockDelay();
      return saveMockEventText(req);
    }
    return this.request<EventText>('/event-texts', {
      method: 'PUT',
      body: JSON.stringify(req),
    });
  }

  /**
   * Die aktiven Depot-Partner, für die Auswahl beim Umordnen einer Vorlage (US-207).
   *
   * Nur für die Zentrale erreichbar — der Server weist jede andere Rolle mit 403 ab.
   * Im Mock-Betrieb leer: es gibt dort keine echten Partnerdaten, und das Umordnen ist
   * ohnehin nicht Teil des Mock-Ablaufs.
   */
  async getPartners(): Promise<Partner[]> {
    if (IS_MOCK) {
      return [];
    }
    const { partners } = await this.request<{ partners: Partner[] }>('/partners');
    return partners;
  }

  /** Löscht eine oder mehrere Vorlagen (Zentrale/Admin). */
  async deleteEventTexts(ids: string[]): Promise<EventTextDeleteResult> {
    if (IS_MOCK) {
      await mockDelay();
      return deleteMockEventTexts(ids);
    }
    return this.request<EventTextDeleteResult>('/event-texts/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  /**
   * Zählt für den Löschdialog, wie viele Veranstaltungen je Vorlage darauf verweisen.
   *
   * Im Mock-Betrieb immer 0: es gibt dort keine echten Verweise, und eine erfundene Zahl
   * im Löschdialog wäre schlimmer als keine.
   */
  async getEventTextUsage(ids: string[]): Promise<EventTextUsageResponse> {
    if (IS_MOCK) {
      await mockDelay();
      return { usage: Object.fromEntries(ids.map((id) => [id, 0])), available: true };
    }
    const query = new URLSearchParams({ ids: ids.join(',') });
    return this.request<EventTextUsageResponse>(`/event-texts/usage?${query}`);
  }

  /**
   * Alle sichtbaren Stornobedingungen (PRD-020 Thema 3).
   *
   * Serverseitig ohne Rollenwache, wortgleich zu getEventTexts: der Zahlungsabschnitt
   * im VA-Formular füllt daraus die Auswahl, für jede Rolle.
   */
  async getCancellationTerms(): Promise<CancellationTerm[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockCancellationTerms();
    }
    return this.request<CancellationTerm[]>('/cancellation-terms');
  }

  /** Legt eine Stornobedingung an oder ändert sie — wortgleich zu saveEventText. */
  async saveCancellationTerm(req: SaveCancellationTermRequest): Promise<CancellationTerm> {
    if (IS_MOCK) {
      await mockDelay();
      return saveMockCancellationTerm(req);
    }
    return this.request<CancellationTerm>('/cancellation-terms', {
      method: 'PUT',
      body: JSON.stringify(req),
    });
  }

  /** Löscht eine oder mehrere Stornobedingungen — wortgleich zu deleteEventTexts. */
  async deleteCancellationTerms(ids: string[]): Promise<CancellationTermDeleteResult> {
    if (IS_MOCK) {
      await mockDelay();
      return deleteMockCancellationTerms(ids);
    }
    return this.request<CancellationTermDeleteResult>('/cancellation-terms/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  /**
   * Zählt für den Löschdialog, wie viele Veranstaltungen je Bedingung darauf verweisen
   * — wortgleich zu getEventTextUsage.
   */
  async getCancellationTermUsage(ids: string[]): Promise<CancellationTermUsageResponse> {
    if (IS_MOCK) {
      await mockDelay();
      return { usage: Object.fromEntries(ids.map((id) => [id, 0])), available: true };
    }
    const query = new URLSearchParams({ ids: ids.join(',') });
    return this.request<CancellationTermUsageResponse>(`/cancellation-terms/usage?${query}`);
  }

  /**
   * Alle Zahlungstext-Vorlagen (Rich-Text-Umstellung, Teil 3).
   *
   * Serverseitig ohne Rollenwache, wortgleich zu getCancellationTerms: der
   * Zahlungsabschnitt im VA-Formular füllt daraus die Auswahl, für jede Rolle.
   */
  async getPaymentNoteTemplates(): Promise<PaymentNoteTemplate[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockPaymentNoteTemplates();
    }
    return this.request<PaymentNoteTemplate[]>('/payment-note-templates');
  }

  /** Legt eine Zahlungstext-Vorlage an oder ändert sie (Zentrale/Admin). */
  async savePaymentNoteTemplate(req: SavePaymentNoteTemplateRequest): Promise<PaymentNoteTemplate> {
    if (IS_MOCK) {
      await mockDelay();
      return saveMockPaymentNoteTemplate(req);
    }
    return this.request<PaymentNoteTemplate>('/payment-note-templates', {
      method: 'PUT',
      body: JSON.stringify(req),
    });
  }

  /** Löscht eine oder mehrere Zahlungstext-Vorlagen (Zentrale/Admin). */
  async deletePaymentNoteTemplates(ids: string[]): Promise<PaymentNoteTemplateDeleteResult> {
    if (IS_MOCK) {
      await mockDelay();
      return deleteMockPaymentNoteTemplates(ids);
    }
    return this.request<PaymentNoteTemplateDeleteResult>('/payment-note-templates/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  /** Alle Einträge der Pflege "Finanzielle Unterstützung" — ohne Rollenwache, für jede Rolle lesbar. */
  async getFinancialSupport(): Promise<FinancialSupport[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockFinancialSupport();
    }
    return this.request<FinancialSupport[]>('/financial-support');
  }

  /** Legt einen Eintrag an oder ändert ihn (Zentrale/Admin). */
  async saveFinancialSupport(req: SaveFinancialSupportRequest): Promise<FinancialSupport> {
    if (IS_MOCK) {
      await mockDelay();
      return saveMockFinancialSupport(req);
    }
    return this.request<FinancialSupport>('/financial-support', {
      method: 'PUT',
      body: JSON.stringify(req),
    });
  }

  /** Löscht einen oder mehrere Einträge (Zentrale/Admin). */
  async deleteFinancialSupport(ids: string[]): Promise<FinancialSupportDeleteResult> {
    if (IS_MOCK) {
      await mockDelay();
      return deleteMockFinancialSupport(ids);
    }
    return this.request<FinancialSupportDeleteResult>('/financial-support/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  /** Zählt für den Löschdialog, wie viele Veranstaltungen je Vorlage darauf verweisen. */
  async getPaymentNoteTemplateUsage(ids: string[]): Promise<PaymentNoteTemplateUsageResponse> {
    if (IS_MOCK) {
      await mockDelay();
      return { usage: Object.fromEntries(ids.map((id) => [id, 0])), available: true };
    }
    const query = new URLSearchParams({ ids: ids.join(',') });
    return this.request<PaymentNoteTemplateUsageResponse>(`/payment-note-templates/usage?${query}`);
  }

  /** Alle Anmeldetext-Vorlagen — wortgleich zu getPaymentNoteTemplates. */
  async getRegistrationNoteTemplates(): Promise<RegistrationNoteTemplate[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockRegistrationNoteTemplates();
    }
    return this.request<RegistrationNoteTemplate[]>('/registration-note-templates');
  }

  /** Legt eine Anmeldetext-Vorlage an oder ändert sie (Zentrale/Admin). */
  async saveRegistrationNoteTemplate(
    req: SaveRegistrationNoteTemplateRequest
  ): Promise<RegistrationNoteTemplate> {
    if (IS_MOCK) {
      await mockDelay();
      return saveMockRegistrationNoteTemplate(req);
    }
    return this.request<RegistrationNoteTemplate>('/registration-note-templates', {
      method: 'PUT',
      body: JSON.stringify(req),
    });
  }

  /** Löscht eine oder mehrere Anmeldetext-Vorlagen (Zentrale/Admin). */
  async deleteRegistrationNoteTemplates(ids: string[]): Promise<RegistrationNoteTemplateDeleteResult> {
    if (IS_MOCK) {
      await mockDelay();
      return deleteMockRegistrationNoteTemplates(ids);
    }
    return this.request<RegistrationNoteTemplateDeleteResult>(
      '/registration-note-templates/bulk-delete',
      {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }
    );
  }

  /** Zählt für den Löschdialog, wie viele Veranstaltungen je Vorlage darauf verweisen. */
  async getRegistrationNoteTemplateUsage(
    ids: string[]
  ): Promise<RegistrationNoteTemplateUsageResponse> {
    if (IS_MOCK) {
      await mockDelay();
      return { usage: Object.fromEntries(ids.map((id) => [id, 0])), available: true };
    }
    const query = new URLSearchParams({ ids: ids.join(',') });
    return this.request<RegistrationNoteTemplateUsageResponse>(
      `/registration-note-templates/usage?${query}`
    );
  }

  /**
   * Legt ein Mailing an oder ändert es (Zentrale/Admin).
   *
   * Identität ist die Bezeichnung, nicht der commercetools-Schlüssel: der wird
   * serverseitig daraus abgeleitet und ist nicht eindeutig umkehrbar. Beim Ändern
   * reist die ursprüngliche Bezeichnung als `previousLabel` mit; eine Umbenennung
   * löst das Backend als Neu-Anlegen + Alt-Löschen.
   */
  async saveMailing(req: SaveMailingRequest): Promise<Mailing> {
    if (IS_MOCK) {
      await mockDelay();
      return saveMockMailing(req);
    }
    return this.request<Mailing>('/mailings', {
      method: 'PUT',
      body: JSON.stringify(req),
    });
  }

  /**
   * Löscht ein oder mehrere Mailings (Zentrale/Admin).
   *
   * POST statt DELETE mit Body: die Mehrfachauswahl braucht ohnehin einen Body, und
   * manche Zwischenschichten verwerfen DELETE-Bodies. Muster wie bei
   * /reviews/bulk-moderate.
   *
   * Antwortet immer mit 200 und trennt `removed` / `notFound` / `failed` — ein
   * bereits gelöschter Eintrag ist kein Fehler, ein echter Teilfehler muss sichtbar
   * bleiben.
   */
  async deleteMailings(titles: string[]): Promise<MailingDeleteResult> {
    if (IS_MOCK) {
      await mockDelay();
      return deleteMockMailings(titles);
    }
    return this.request<MailingDeleteResult>('/mailings/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ titles }),
    });
  }

  /**
   * Uploads the mailing spreadsheet (admin only).
   *
   * The file is base64-encoded into a JSON body rather than sent as multipart:
   * `request()` sets `Content-Type: application/json` for everything, and
   * FormData would need that header *removed* so the browser can set the
   * multipart boundary. At ~12 KB the encoding overhead does not matter, and this
   * way the upload keeps the shared auth headers and error unwrapping.
   */
  async importMailings(file: File): Promise<MailingImportResult> {
    const dataBase64 = await fileToBase64(file);
    return this.request<MailingImportResult>('/mailings/import', {
      method: 'POST',
      body: JSON.stringify({ filename: file.name, dataBase64 }),
    });
  }

  /**
   * Teilnehmerliste einer Veranstaltung.
   *
   * Kein IS_MOCK-Zweig: die Liste kommt aus der ticket-api, und ein erfundener Mock
   * würde eine Anwesenheitsliste vortäuschen, die es nicht gibt. Ohne Backend meldet
   * der Aufruf einen Fehler — das ist hier die ehrlichere Antwort.
   */
  async getParticipants(eventId: string): Promise<ParticipantsResponse> {
    return this.request<ParticipantsResponse>(
      `/events/${encodeURIComponent(eventId)}/participants`
    );
  }

  /** Storniert ein Ticket aus der Teilnehmerliste; nur die Zentrale, 204 ohne Body. */
  async cancelTicket(eventId: string, ticketId: string): Promise<void> {
    await this.request<void>(
      `/events/${encodeURIComponent(eventId)}/tickets/${encodeURIComponent(ticketId)}`,
      { method: 'DELETE' }
    );
  }

  // --- Abrechnung (DCP-2228, 08.09.2026) ----------------------------------
  //
  // Kein IS_MOCK-Zweig, gleiche Begründung wie bei getParticipants: die
  // Ticket-api-Berechnung (Staffel/Quote) ließe sich nicht sinnvoll nachbilden,
  // ohne den Rechner ein zweites Mal zu schreiben.

  async getRefund(eventId: string): Promise<RefundView> {
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund`);
  }

  async startRefund(eventId: string): Promise<RefundView> {
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund`, {
      method: 'POST',
    });
  }

  async saveRefundDraft(
    eventId: string,
    draft: Pick<RefundDraft, 'participants' | 'wines'>
  ): Promise<RefundDraft> {
    return this.request<RefundDraft>(`/events/${encodeURIComponent(eventId)}/refund/draft`, {
      method: 'PUT',
      body: JSON.stringify(draft),
    });
  }

  async submitRefund(eventId: string): Promise<RefundView> {
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund/submit`, {
      method: 'POST',
    });
  }

  /** "Abrechnung freigeben & übertragen" (DCP-2228 follow-up, 16.09.2026). */
  async transferRefund(eventId: string): Promise<RefundView> {
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund/transfer`, {
      method: 'POST',
    });
  }

  /** "Tickets nachpflegen" — a walk-in participant who never had a ticket. */
  async addRefundParticipant(
    eventId: string,
    input: AddRefundParticipantInput
  ): Promise<RefundView> {
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund/participants`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async cancelRefundTicket(eventId: string, ticketId: string): Promise<RefundView> {
    return this.request<RefundView>(
      `/events/${encodeURIComponent(eventId)}/refund/tickets/${encodeURIComponent(ticketId)}`,
      { method: 'DELETE' }
    );
  }

  /** Schritt 3.3's numbers, computed but not persisted — see previewRefund's backend counterpart. */
  async previewRefund(eventId: string): Promise<Settlement> {
    return this.request<Settlement>(`/events/${encodeURIComponent(eventId)}/refund/preview`, {
      method: 'POST',
    });
  }

  // Nachweis-Upload in der Abrechnung (DCP-2228 follow-up, 15.09.2026) — selbes
  // Base64-in-JSON-Muster wie uploadEventImport().
  async uploadRefundProof(eventId: string, file: File): Promise<RefundView> {
    const dataBase64 = await fileToBase64(file);
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund/proof`, {
      method: 'PUT',
      body: JSON.stringify({ filename: file.name, dataBase64 }),
    });
  }

  /** Nachweis entfernen, ohne einen neuen hochzuladen (21.09.2026-Follow-up). */
  async deleteRefundProof(eventId: string): Promise<RefundView> {
    return this.request<RefundView>(`/events/${encodeURIComponent(eventId)}/refund/proof`, {
      method: 'DELETE',
    });
  }

  /** Freigabe durch Admin/Zentrale (`in_review_proof` → `in_review`), 21.09.2026-Follow-up. */
  async approveRefundProof(eventId: string): Promise<TicketRefund> {
    return this.request<TicketRefund>(`/events/${encodeURIComponent(eventId)}/refund/approve-proof`, {
      method: 'POST',
    });
  }

  /** Für einen Herunterladen-/Anzeigen-Link — kein request(), das nimmt JSON an. */
  async getRefundProofBlob(eventId: string): Promise<Blob> {
    const headers = await this.buildAuthHeaders();
    const response = await fetch(
      `${this.baseUrl}/events/${encodeURIComponent(eventId)}/refund/proof/file`,
      { headers }
    );
    if (!response.ok) {
      throw new ApiError(response.status, `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.blob();
  }

  async searchWineProducts(query: string): Promise<WineProduct[]> {
    const response = await this.request<{ items: WineProduct[] }>(
      `/products/search?q=${encodeURIComponent(query)}`
    );
    return response.items;
  }

  /** Resolved lazily, only when a wine is actually added to the Abrechnung draft. */
  async resolveWineTaxCategory(sku: string): Promise<TaxCategoryKey> {
    const response = await this.request<{ taxCategory: TaxCategoryKey }>(
      `/products/${encodeURIComponent(sku)}/tax-category`
    );
    return response.taxCategory;
  }

  /** Customer picker for "Tickets nachpflegen" — searches the CDP by number or name. */
  async searchCustomers(query: string): Promise<CustomerSearchResult[]> {
    const response = await this.request<{ items: CustomerSearchResult[] }>(
      `/customers/search?q=${encodeURIComponent(query)}`
    );
    return response.items;
  }

  // --- VA-Massenimport (05.08.2026) ---------------------------------------
  //
  // Kein IS_MOCK-Zweig, gleiche Begründung wie bei getParticipants: der ganze
  // Witz ist die asynchrone Verarbeitung durch die Worker-Lambda, die ein Mock
  // nicht sinnvoll nachbilden kann, ohne selbst einen kleinen Worker zu bauen.

  async listEventImportJobs(): Promise<ImportJobStatusFile[]> {
    return this.request<ImportJobStatusFile[]>('/events/import');
  }

  /**
   * Erzeugt den Job — läuft NACH uploadZip(), mit dessen jobId (20.09.2026:
   * ein kombiniertes ZIP aus Excel-Vorlage + Flyer-PDFs, keine Datei-Bytes
   * mehr in diesem Request). Kein Base64-in-JSON mehr für diesen Endpoint.
   */
  async uploadEventImport(
    jobId: string,
    filename: string,
    vaType: string,
    targetStatus: 'initial' | 'approved'
  ): Promise<UploadEventsImportResult> {
    return this.request<UploadEventsImportResult>('/events/import', {
      method: 'POST',
      body: JSON.stringify({ jobId, filename, vaType, targetStatus }),
    });
  }

  /**
   * Presigned S3-PUT-URL für das kombinierte Import-ZIP (Excel-Vorlage +
   * Flyer-PDFs, 18.-20.09.2026).
   *
   * Läuft VOR uploadEventImport(): die zurückgegebene jobId wandert in den
   * folgenden uploadEventImport()-Aufruf, damit beide Schritte zu demselben
   * Job gehören. Der eigentliche PUT geht direkt an S3, nicht über
   * this.request() — das ist eine fremde URL, keine unserer eigenen
   * API-Routen, und braucht keinen Auth-Header (die Signatur in der URL
   * übernimmt das).
   */
  async requestUploadUrl(): Promise<UploadUrlResult> {
    return this.request<UploadUrlResult>('/events/import/upload-url', {
      method: 'POST',
    });
  }

  /** Lädt das Import-ZIP direkt zu S3 hoch — siehe requestUploadUrl(). */
  async uploadZip(uploadUrl: string, zipFile: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/zip' },
      body: zipFile,
    });
    if (!response.ok) {
      throw new ApiError(response.status, `ZIP-Upload fehlgeschlagen: HTTP ${response.status}`);
    }
  }

  async startEventImportJob(jobId: string): Promise<ImportJobStatusFile> {
    return this.request<ImportJobStatusFile>(
      `/events/import/${encodeURIComponent(jobId)}/start`,
      { method: 'POST' }
    );
  }

  async stopEventImportJob(jobId: string): Promise<ImportJobStatusFile> {
    return this.request<ImportJobStatusFile>(
      `/events/import/${encodeURIComponent(jobId)}/stop`,
      { method: 'POST' }
    );
  }

  async rollbackEventImportJob(jobId: string): Promise<RollbackImportResult> {
    return this.request<RollbackImportResult>(
      `/events/import/${encodeURIComponent(jobId)}/rollback`,
      { method: 'POST' }
    );
  }

  async deleteEventImportJob(jobId: string): Promise<void> {
    await this.request<void>(`/events/import/${encodeURIComponent(jobId)}`, {
      method: 'DELETE',
    });
  }

  // --- Benutzerverwaltung (nur Admin) -------------------------------------

  async getUsers(): Promise<ManagedUser[]> {
    return this.request<ManagedUser[]>('/users');
  }

  async getPermissionCatalog(): Promise<PermissionCatalog> {
    return this.request<PermissionCatalog>('/permission-catalog');
  }

  async saveUser(user: ManagedUser): Promise<ManagedUser> {
    return this.request<ManagedUser>(`/users/${encodeURIComponent(user.email)}`, {
      method: 'PUT',
      body: JSON.stringify({
        role: user.role,
        permissions: user.permissions,
        canSwitchRole: user.canSwitchRole ?? false,
      }),
    });
  }

  async deleteUser(email: string): Promise<void> {
    return this.request<void>(`/users/${encodeURIComponent(email)}`, { method: 'DELETE' });
  }

  /**
   * Assumes another role for testing (staging only).
   *
   * Stores the returned token so every later request carries it. The server decides
   * whether the switch is allowed; a rejection surfaces as an ApiError.
   */
  async switchRole(role: Role, depots: string[]): Promise<RoleSwitchResult> {
    const result = await this.request<RoleSwitchResult>('/session/role', {
      method: 'POST',
      body: JSON.stringify({ role, depots }),
    });

    this.setAssumedRoleToken(result.token);
    return result;
  }

  async getDepots(): Promise<DepotRef[]> {
    if (IS_MOCK) {
      await mockDelay();
      return getMockDepots();
    }
    return this.request<DepotRef[]>('/depots');
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
